import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppPicker } from "@/modules/shell/AppPicker";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import {
  linkPartyPerson,
  listParties,
  unlinkPartyPerson,
  type PartyPersonLink,
} from "@/shared/api/parties";
import { notify } from "@/shared/feedback/notify";

const LINK_LABELS = ["Purchasing", "Accounts", "Owner", "Other"] as const;

type Props = {
  companySlug: string;
  orgPartyId: string;
  people: PartyPersonLink[];
  canEdit: boolean;
  onChanged: () => Promise<unknown> | void;
};

export function ContactPeople({
  companySlug,
  orgPartyId,
  people,
  canEdit,
  onChanged,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState<string>("Purchasing");
  const [pickId, setPickId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [personOptions, setPersonOptions] = useState<
    Array<{ value: string; label: string; keywords?: string }>
  >([]);

  useEffect(() => {
    let cancelled = false;
    void listParties(companySlug, { kind: "person", limit: 100 })
      .then((page) => {
        if (cancelled) return;
        const linked = new Set(people.map((p) => p.personPartyId));
        setPersonOptions(
          page.parties
            .filter((p) => p.id !== orgPartyId && !linked.has(p.id))
            .map((p) => ({
              value: p.id,
              label: p.name,
              keywords: [p.name, p.code ?? "", p.primaryPhone ?? ""]
                .filter(Boolean)
                .join(" "),
            })),
        );
      })
      .catch(() => {
        if (!cancelled) setPersonOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, orgPartyId, people]);

  async function run(fn: () => Promise<unknown>, okMsg: string) {
    setBusy(true);
    try {
      await fn();
      notify.success(okMsg);
      await onChanged();
    } catch (err: unknown) {
      notify.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <FieldGroup
      title="People"
      className="max-w-lg"
      description="Contact people at this organization — phones stay on their own card"
    >
      {people.length === 0 ? (
        <p className="m-0 text-sm text-[var(--color-muted)]">
          No people linked yet.
        </p>
      ) : (
        <ul className="m-0 list-none space-y-3 p-0">
          {people.map((p) => (
            <li key={p.id} className="space-y-0.5 text-sm">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-[var(--color-muted)]">{p.label}</span>
                <Link
                  to={`/c/${companySlug}/contacts/${p.personPartyId}`}
                  className="no-underline"
                >
                  {p.personName}
                </Link>
                {canEdit ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () =>
                          unlinkPartyPerson(companySlug, orgPartyId, p.id),
                        "Person unlinked",
                      )
                    }
                  >
                    Unlink
                  </Button>
                ) : null}
              </div>
              {p.primaryPhone ? (
                <div>
                  <a
                    href={`tel:${p.primaryPhone}`}
                    className="font-mono text-xs no-underline"
                  >
                    {p.primaryPhone}
                  </a>
                </div>
              ) : null}
              {p.primaryEmail ? (
                <div>
                  <a
                    href={`mailto:${p.primaryEmail}`}
                    className="text-xs no-underline"
                  >
                    {p.primaryEmail}
                  </a>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <div className="space-y-3 border-t border-[var(--color-border)]/60 pt-3">
          <div className="space-y-1">
            <Label htmlFor="people-label">Role label</Label>
            <select
              id="people-label"
              className="h-9 rounded-md border border-[var(--color-border)] bg-transparent px-2 text-sm"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            >
              {LINK_LABELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>

          <FieldGroup title="Link existing" level="sub">
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[12rem] flex-1">
                <AppPicker
                  title="Person"
                  description="Link an existing person Contact"
                  placeholder="Choose person"
                  value={pickId}
                  disabled={busy}
                  options={personOptions}
                  emptyLabel="No people to link"
                  onChange={(id) => setPickId(id)}
                />
              </div>
              <Button
                type="button"
                size="sm"
                disabled={busy || !pickId}
                onClick={() =>
                  void run(async () => {
                    await linkPartyPerson(companySlug, orgPartyId, {
                      personPartyId: pickId!,
                      label,
                    });
                    setPickId(null);
                  }, "Person linked")
                }
              >
                Link
              </Button>
            </div>
          </FieldGroup>

          <FieldGroup title="New person & link" level="sub">
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[12rem] flex-1 space-y-1">
                <Label htmlFor="people-new-name">Name</Label>
                <Input
                  id="people-new-name"
                  value={newName}
                  disabled={busy}
                  placeholder="Maria Santos"
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
              <Button
                type="button"
                size="sm"
                disabled={busy || !newName.trim()}
                onClick={() =>
                  void run(async () => {
                    await linkPartyPerson(companySlug, orgPartyId, {
                      newPerson: { name: newName.trim() },
                      label,
                    });
                    setNewName("");
                  }, "Person created & linked")
                }
              >
                Add
              </Button>
            </div>
          </FieldGroup>
        </div>
      ) : null}
    </FieldGroup>
  );
}
